import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, BookOpen, Headphones, Quote, Users, PenLine } from "lucide-react";
import Nav from "@/components/Nav";
import Footer, { SOCIALS } from "@/components/Footer";
import { BOOK, LAWS, REVIEWS } from "@/lib/book";
import AboutBook from "@/components/AboutBook";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Reusable scroll-revealed soft fade-up — for static text sections
const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

function CinematicDivider({ image, overline, headline, eyebrow, height = "h-[60vh] sm:h-[70vh]", testid }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Subtle parallax: image drifts up ~80px as the section scrolls past
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.0]);

  return (
    <section ref={ref} className="relative w-full bg-black overflow-hidden" data-testid={testid}>
      {/* MOBILE: stacked layout — image shows in full, text below */}
      <div className="sm:hidden">
        <motion.img
          style={{ scale }}
          src={image}
          alt=""
          className="w-full h-auto object-contain will-change-transform"
          loading="lazy"
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          className="px-6 py-10 bg-[#0A0A0A]"
        >
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-3">
              {eyebrow}
            </div>
          )}
          {overline && (
            <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 font-mono-plex">
              {overline}
            </div>
          )}
          <h3 className="font-display font-black tracking-tighter text-white text-3xl leading-[0.95] text-balance">
            {headline}
          </h3>
        </motion.div>
      </div>

      {/* DESKTOP / TABLET: cinematic overlay layout */}
      <div className={`hidden sm:block relative w-full ${height.replace(/h-\[[^\]]+\]\s*/, "")} overflow-hidden`}>
        <motion.img
          style={{ y, scale }}
          src={image}
          alt=""
          className="absolute inset-0 w-full h-[112%] object-cover will-change-transform"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" aria-hidden />
        <div className="relative max-w-7xl mx-auto h-full px-6 sm:px-10 flex flex-col justify-end pb-14 sm:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">
                {eyebrow}
              </div>
            )}
            {overline && (
              <div className="text-sm uppercase tracking-[0.2em] text-white/70 mb-3 font-mono-plex">
                {overline}
              </div>
            )}
            <h3 className="font-display font-black tracking-tighter text-white text-5xl lg:text-6xl leading-[0.95] text-balance">
              {headline}
            </h3>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-[#D92D20] text-[#D92D20]" />
      ))}
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-12 gap-x-6 gap-y-12 items-end">
            <motion.div
              initial="hidden"
              animate="show"
              variants={stagger}
              className="col-span-12 lg:col-span-7"
            >
              <motion.div variants={fadeUp} className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-6">
                A new lens on modern dating · Out Now
              </motion.div>

              {/* Social proof bar — above the fold */}
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-7 text-sm" data-testid="social-proof-bar">
                <div className="flex items-center gap-2">
                  <Stars />
                  <span className="font-medium">Rated 5 stars</span>
                  <span className="text-[#525252]">by readers on Amazon</span>
                </div>
                <span className="hidden sm:block w-px h-4 bg-black/15" />
                <div className="inline-flex items-center gap-2 text-[#525252]">
                  <Users className="w-4 h-4 text-[#D92D20]" />
                  Hundreds of readers already purchased
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="mb-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h1 className="font-display font-black tracking-tight text-2xl sm:text-3xl leading-tight" data-testid="hero-book-title">
                  {BOOK.title}
                </h1>
                <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#525252]" data-testid="hero-book-author">
                  By {BOOK.author}
                </span>
              </motion.div>

              <motion.p
                variants={fadeUp}
                className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-[88px] leading-[0.92] text-balance"
                data-testid="hero-title"
              >
                Modern dating<br />isn't random.<br />
                <span className="text-[#D92D20]">It follows patterns.</span>
              </motion.p>

              <motion.p variants={fadeUp} className="mt-6 max-w-xl text-base sm:text-lg leading-snug text-[#0A0A0A] font-medium border-l-2 border-[#D92D20] pl-4" data-testid="hero-tagline">
                The controversial relationship book changing how people understand connections, emotional investment, and power.
              </motion.p>

              <motion.p variants={fadeUp} className="mt-6 max-w-xl text-lg leading-relaxed text-[#525252]">
                Why do some people always seem to have the upper hand in relationships — while others feel like they're constantly chasing, over-investing, or getting emotionally drained?
                <span className="block mt-4 text-[#0A0A0A]">
                  The 12 Laws of Dating Dynamics breaks down the hidden rules that quietly control attraction, emotional investment, and relationship outcomes.
                </span>
              </motion.p>

              <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/checkout"
                  className="group inline-flex items-center gap-3 bg-[#D92D20] text-white px-7 py-4 text-base font-medium hover:bg-[#B42318] transition-colors"
                  data-testid="hero-buy-btn"
                >
                  Order Your Copy — $19.99
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#laws"
                  className="inline-flex items-center gap-2 border border-[#0A0A0A] px-6 py-4 text-sm font-medium hover:bg-[#0A0A0A] hover:text-[#FFFFFF] transition-colors"
                  data-testid="hero-laws-btn"
                >
                  Read the 12 Laws
                </a>
              </motion.div>

              {/* Signed copy incentive — only available here */}
              <motion.div
                variants={fadeUp}
                className="mt-6 inline-flex items-center gap-3 bg-[#FFF7F6] border border-[#D92D20]/30 px-4 py-3 text-sm"
                data-testid="signed-copy-incentive"
              >
                <PenLine className="w-4 h-4 text-[#D92D20] shrink-0" />
                <span><span className="font-bold text-[#0A0A0A]">Signed by Manuel Santana</span> — only when you order direct. Not on Amazon.</span>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8 text-sm text-[#525252]">
                Worldwide shipping · Stripe secure checkout
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-5 flex justify-center lg:justify-end"
            >
              <div className="relative group">
                <motion.div
                  className="absolute -top-6 -left-6 w-32 h-32 bg-[#D92D20] -z-10"
                  aria-hidden
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                />
                <img
                  src={BOOK.cover}
                  alt={`${BOOK.title} book cover`}
                  className="book-shadow w-[280px] sm:w-[340px] lg:w-[380px] h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.025] group-hover:-rotate-[0.5deg]"
                  data-testid="hero-book-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="border-y border-black/10 bg-[#0A0A0A] text-[#FFFFFF] overflow-hidden">
          <div className="marquee whitespace-nowrap flex gap-12 py-3 text-[11px] uppercase tracking-[0.3em] font-medium">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex gap-12">
                <span>Power · Attraction · Investment · Frame · Polarity</span>
                <span className="text-[#D92D20]">★</span>
                <span>Stop chasing. Start understanding.</span>
                <span className="text-[#D92D20]">★</span>
                <span>By Manuel Santana</span>
                <span className="text-[#D92D20]">★</span>
                <span>Penmier Publishing · 2025</span>
                <span className="text-[#D92D20]">★</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO EDITORIAL */}
      <section className="border-b border-black/10" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6">
            <Reveal className="col-span-12 md:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">§ 01 — Why this book</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
                Power isn't something you take.<br />
                <span className="text-[#525252]">It's something you misplace.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="col-span-12 md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-[#0A0A0A]">
              <p>
                Why do connections start strong, then slowly become one-sided? Why does modern dating feel more like a power struggle than a partnership?
              </p>
              <p className="text-[#525252]">
                This isn't generic dating advice. This is a deeper look at the psychological and behavioral patterns that shape who has power — and who doesn't. The kind of patterns you can feel but rarely name.
              </p>
              <p>
                Whether you're tired of being the one who cares more, trying to understand why your relationships don't last, or simply want to see dating with more clarity — this book gives you a new lens to understand what's really going on.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <AboutBook />

      {/* CINEMATIC DIVIDER 1 — emotional invisibility */}
      <CinematicDivider
        image="/images/divider_invisible.jpg"
        eyebrow="A modern relationship paradox"
        headline={<>You can sit next to someone<br /><span className="text-[#D92D20]">and still feel invisible.</span></>}
        testid="cinematic-divider-1"
      />

      {/* WHO THIS BOOK IS FOR */}
      <section id="for-who" className="border-b border-black/10" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">§ 02 — Who this book is for</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
                If any of this sounds like you,<br />
                <span className="text-[#525252]">this book was written for you.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:pt-6 text-[#525252] leading-relaxed">
              Written for both men and women. No gender takes sides — every chapter applies to anyone trying to understand the unspoken rules of modern relationships.
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5">
            {[
              { n: "01", title: "You're tired of being the one who cares more", body: "You keep finding yourself over-investing emotionally — texting first, planning everything, holding the relationship together while they coast." },
              { n: "02", title: "Your connections start hot, then go cold", body: "Everything feels electric at first — then somewhere along the way, the energy flips and you can't figure out exactly when or why." },
              { n: "03", title: "You want clarity, not generic dating advice", body: "You're not looking for surface-level tips. You want the underlying psychology — the patterns no one openly talks about." },
              { n: "04", title: "You're done with one-sided relationships", body: "You've had enough of being the pursuant, the convenient option, or the emotional support line. You want partnership, not performance." },
              { n: "05", title: "You're a man trying to understand women — or a woman trying to understand men", body: "This book is intentionally balanced. It explains both perspectives without favoring either side, so you finally see the full picture." },
              { n: "06", title: "You're ready to stop guessing and start seeing", body: "You want a framework — laws you can name, recognize, and apply — so dating stops feeling random and starts feeling readable." },
            ].map((item, idx) => (
              <motion.div
                key={item.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className="col-span-12 sm:col-span-6 lg:col-span-4 bg-white border border-black/10 p-7 hover:border-[#D92D20] transition-colors"
                data-testid={`audience-card-${item.n}`}
              >
                <div className="font-mono-plex text-[11px] tracking-[0.2em] text-[#D92D20] mb-5">FOR YOU IF — {item.n}</div>
                <h3 className="font-display font-bold text-xl leading-snug tracking-tight mb-3">{item.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 border-t border-black/10 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="max-w-md text-[#525252]">
              If you saw yourself in any of those, the rest of this page will feel like it was written for you.
            </p>
            <Link
              to="/checkout"
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-7 py-4 font-medium hover:bg-[#D92D20] transition-colors"
              data-testid="audience-buy-btn"
            >
              Order Your Copy — $19.99 <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* PURSUIT & WITHDRAWAL — editorial visual essay */}
      <section className="border-b border-black/10 bg-[#0A0A0A] text-white" data-testid="pursuit-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">An invisible pattern</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
                One reaches.<br />
                <span className="text-[#525252]">The other withdraws.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:pt-8 text-[#A3A3A3] leading-relaxed">
              At every moment of a relationship there is a pursuer and a pursued. The roles reverse the second the pursuant begins to flee. Most people never see it happening — until it has already happened.
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4 sm:gap-5">
            <motion.figure
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-7 relative aspect-video lg:aspect-[5/4] overflow-hidden group"
            >
              <img src="/images/pursuit_chase.jpg" alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden />
              <figcaption className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 font-mono-plex">07 / The Law of Running & Chasing</div>
                <div className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight max-w-md">
                  "The pursuant always invests more — until the moment they stop pursuing."
                </div>
              </figcaption>
            </motion.figure>
            <motion.figure
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-5 relative aspect-video lg:aspect-[5/4] overflow-hidden group"
            >
              <img src="/images/pursuit_withdrawal.jpg" alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden />
              <figcaption className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 font-mono-plex">08 / The Law of Withholding & Activating</div>
                <div className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight max-w-md">
                  "Restraint creates pull. Availability erodes it."
                </div>
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </section>

      {/* THE 12 LAWS - BENTO */}
      <section id="laws" className="border-b border-black/10 bg-[#0A0A0A] text-[#FFFFFF]" data-testid="laws-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">§ 03 — Inside the book</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.9]">
                The 12 Laws<br />of Dating Dynamics
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:pt-8 text-[#A3A3A3] leading-relaxed">
              Each chapter reveals one invisible behavior that shifts power in modern dating — and the antidote to keep your relationships balanced, mutually beneficial, and built on real desire.
            </div>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
            className="grid grid-cols-12 gap-4 sm:gap-5"
          >
            {LAWS.map((law, i) => {
              // Asymmetric tetris grid
              const spans = [
                "col-span-12 sm:col-span-6 lg:col-span-5",
                "col-span-12 sm:col-span-6 lg:col-span-4",
                "col-span-12 sm:col-span-12 lg:col-span-3",
                "col-span-12 sm:col-span-6 lg:col-span-4",
                "col-span-12 sm:col-span-6 lg:col-span-5",
                "col-span-12 sm:col-span-12 lg:col-span-3",
                "col-span-12 sm:col-span-6 lg:col-span-3",
                "col-span-12 sm:col-span-6 lg:col-span-5",
                "col-span-12 sm:col-span-12 lg:col-span-4",
                "col-span-12 sm:col-span-6 lg:col-span-4",
                "col-span-12 sm:col-span-6 lg:col-span-3",
                "col-span-12 sm:col-span-12 lg:col-span-5",
              ];
              return (
                <motion.article
                  key={law.n}
                  variants={fadeUp}
                  whileHover={{ y: -4 }}
                  transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className={`${spans[i]} bg-[#111111] border border-white/10 p-7 hover:border-[#D92D20] transition-colors group`}
                  data-testid={`law-card-${law.n}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-mono-plex text-[11px] tracking-[0.2em] text-[#737373]">LAW {law.n}</span>
                    <ArrowUpRight className="w-4 h-4 text-[#737373] group-hover:text-[#D92D20] group-hover:rotate-12 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-2xl leading-tight tracking-tight mb-3">{law.title}</h3>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed">{law.body}</p>
                </motion.article>
              );
            })}
          </motion.div>

          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/10 pt-10">
            <div className="text-[#A3A3A3] max-w-md">
              <span className="text-[#FFFFFF]">12 laws. 305 pages.</span> One framework to stop guessing and start seeing dating clearly.
            </div>
            <Link
              to="/checkout"
              className="inline-flex items-center gap-3 bg-[#D92D20] text-white px-7 py-4 font-medium hover:bg-[#B42318] transition-colors"
              data-testid="laws-buy-btn"
            >
              Order Your Copy — $19.99 <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CINEMATIC DIVIDER 3 — clarity / unsee */}
      <CinematicDivider
        image="/images/divider_unsee.jpg"
        eyebrow="The shift"
        headline={<>Once you see the laws,<br /><span className="text-[#D92D20]">you can't unsee them.</span></>}
        height="h-[60vh] sm:h-[70vh]"
        testid="cinematic-divider-3"
      />

      {/* REVIEWS */}
      <section id="reviews" className="border-b border-black/10" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 mb-14 items-end">
            <div className="col-span-12 md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">§ 04 — Reader response</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
                A 5.0-star debut.<br />
                <span className="text-[#525252]">Insightful. Balanced. Practical.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 flex md:justify-end">
              <div className="flex items-center gap-4 border border-black/10 bg-white px-5 py-4">
                <Stars />
                <div className="font-mono-plex text-xs uppercase tracking-wider text-[#525252]">
                  5.0 / 5 · 9 reviews on Amazon
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
            className="grid grid-cols-12 gap-5"
          >
            {REVIEWS.map((r, i) => (
              <motion.figure
                key={i}
                variants={fadeUp}
                className={`${i % 5 === 0 ? "col-span-12 lg:col-span-7" : i % 4 === 1 ? "col-span-12 sm:col-span-6 lg:col-span-5" : "col-span-12 sm:col-span-6 lg:col-span-4"} bg-white border border-black/10 p-7 flex flex-col gap-5`}
                data-testid={`review-card-${i}`}
              >
                <Quote className="w-6 h-6 text-[#D92D20]" />
                <blockquote className="text-[#0A0A0A] leading-relaxed text-lg">
                  "{r.quote}"
                </blockquote>
                <figcaption className="mt-auto flex items-center justify-between pt-4 border-t border-black/10">
                  <div className="font-medium">{r.name}</div>
                  <Stars />
                </figcaption>
              </motion.figure>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CINEMATIC DIVIDER 2 — emotional distance (moved here to spread out cinematic moments) */}
      <CinematicDivider
        image="/images/divider_distance.jpg"
        eyebrow="The space between two people"
        headline={<>The room is the same.<br /><span className="text-[#D92D20]">The distance is not.</span></>}
        height="h-[55vh] sm:h-[65vh]"
        testid="cinematic-divider-2"
      />

      {/* AUTHOR */}
      <section id="author" className="border-b border-black/10 bg-white" data-testid="author-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 items-start">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="relative group overflow-hidden">
                <div className="absolute -top-4 -left-4 w-28 h-28 border border-[#D92D20] z-10" aria-hidden />
                <img
                  src="https://customer-assets.emergentagent.com/job_dating-dynamics-book/artifacts/7kzlpom5_bm8i9524-edit.png"
                  alt="Manuel Santana — author portrait"
                  className="relative w-full max-w-md book-shadow transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                  data-testid="author-portrait"
                />
                <div className="mt-3 text-xs font-mono-plex text-[#525252]">
                  Manuel Santana
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="col-span-12 md:col-span-6 md:col-start-7">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">§ 05 — The author</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]" data-testid="author-heading">
                <span className="block text-2xl sm:text-3xl text-[#525252] mb-2">About the Author,</span>
                Manuel Santana
              </h2>
              <div className="mt-3 flex flex-wrap gap-3 text-xs uppercase tracking-[0.2em] text-[#525252]">
                <span className="border border-black/15 px-3 py-1">Relationship Coach</span>
                <span className="border border-black/15 px-3 py-1">Author</span>
                <span className="border border-black/15 px-3 py-1">Podcaster</span>
              </div>
              <div className="mt-8 space-y-5 text-lg leading-relaxed text-[#0A0A0A]">
                <p>
                  Manuel Santana is a Relationship Coach, Author, and Podcaster. From a young age, he began taking notes on human behavior and his own relationship experiences — both good and bad.
                </p>
                <p className="text-[#525252]">
                  In the summer of 2024 he launched <span className="text-[#0A0A0A] font-medium inline-flex items-center gap-1"><Headphones className="w-4 h-4" /> Prove Your Point Podcast</span> to share his message and pursue his passion: helping people experience a better dating life.
                </p>
                <p>
                  He is an emerging thought-leader in the conversation around intersexual dynamics in modern-day society — and has several more books in the works on dating, love, and relationships.
                </p>
              </div>
              <div className="mt-10">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-4">Follow @IAmManuelSantana</div>
                <div className="flex flex-wrap gap-3">
                  {SOCIALS.map(({ name, url, Icon }) => (
                    <a
                      key={name}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 text-xs uppercase tracking-[0.18em] hover:bg-[#D92D20] transition-colors"
                      data-testid={`author-social-${name.toLowerCase()}`}
                    >
                      <Icon className="w-4 h-4" />
                      {name}
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BOOK DETAILS + BUY */}
      <section id="details" className="border-b border-black/10" data-testid="details-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6">
            <Reveal className="col-span-12 lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">§ 06 — The book</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
                {BOOK.title}.
              </h2>
              <p className="mt-4 text-xl text-[#525252] leading-snug max-w-md">{BOOK.subtitle}.</p>

              <dl className="mt-10 grid grid-cols-2 gap-x-4 gap-y-6 text-sm">
                {[
                  ["Format", BOOK.format],
                  ["Pages", `${BOOK.pages}`],
                  ["Publisher", BOOK.publisher],
                  ["Published", BOOK.published],
                  ["Dimensions", BOOK.dimensions],
                  ["Weight", BOOK.weight],
                  ["Language", BOOK.language],
                  ["ISBN-13", BOOK.isbn],
                ].map(([k, v]) => (
                  <div key={k} className="border-t border-black/10 pt-3">
                    <dt className="text-[10px] uppercase tracking-[0.25em] text-[#525252]">{k}</dt>
                    <dd className="mt-1 font-medium font-mono-plex text-sm">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={0.15} className="col-span-12 lg:col-span-6 lg:col-start-7">
              <div className="bg-[#0A0A0A] text-[#FFFFFF] p-8 sm:p-10">
                <div className="flex items-start gap-6">
                  <BookOpen className="w-8 h-8 text-[#D92D20] shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[#D92D20]">Order direct · Signed</div>
                    <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2 leading-[0.95]">
                      Signed Paperback · $19.99
                    </h3>
                    <p className="mt-4 text-[#A3A3A3] max-w-md leading-relaxed">
                      Personally signed by Manuel Santana — only when you order here. Ships worldwide via tracked carriers, live rates at checkout.
                    </p>
                  </div>
                </div>
                <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm text-[#A3A3A3]">
                  <li className="flex items-center gap-2"><span className="text-[#D92D20]">●</span> Signed by Manuel Santana</li>
                  <li className="flex items-center gap-2"><span className="text-[#D92D20]">●</span> Worldwide shipping</li>
                  <li className="flex items-center gap-2"><span className="text-[#D92D20]">●</span> Stripe secure checkout</li>
                  <li className="flex items-center gap-2"><span className="text-[#D92D20]">●</span> Tracked delivery</li>
                </ul>
                <Link
                  to="/checkout"
                  className="mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#D92D20] text-white px-8 py-4 text-base font-medium hover:bg-[#B42318] transition-colors"
                  data-testid="details-buy-btn"
                >
                  Order Your Copy
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
